import {
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  applyVclCssVars,
  buildCssVarsForControl,
  buildVclCssVars,
  getEffectiveState,
} from './applyVclCssVars.js';
import { createBaselineState, isBaselineState, VCL_BASELINE_STEPS } from './baseline.js';
import { getVclControl } from './controls.js';
import {
  clearVclStateStorage,
  readVclStateFromStorage,
  shouldMountVcl,
  writeVclStateToStorage,
} from './storage.js';
import type {
  VclClampNote,
  VclControlState,
  VclExportPayload,
  VclPromotionDelta,
} from './types.js';
import { VCL_CONTROL_IDS } from './types.js';
import { VclContext, type VclContextValue } from './VclContext.js';
import { VclDevPanel } from './VclDevPanel.js';

export type { VclContextValue } from './VclContext.js';
export { useVcl, useVclOptional } from './VclContext.js';

function buildExportPayload(
  requested: VclControlState,
  effective: VclControlState,
  clamps: readonly VclClampNote[],
): VclExportPayload {
  const deltas: VclPromotionDelta[] = [];
  for (const id of VCL_CONTROL_IDS) {
    if (effective[id] === VCL_BASELINE_STEPS[id] && requested[id] === VCL_BASELINE_STEPS[id]) {
      continue;
    }
    const def = getVclControl(id);
    deltas.push({
      controlId: id,
      promotionTarget: def.promotionTarget,
      requestedStep: requested[id],
      effectiveStep: effective[id],
      baselineStep: VCL_BASELINE_STEPS[id],
      cssVars: buildCssVarsForControl(id, effective),
    });
  }
  return {
    surface: 'customer',
    exportedAt: new Date().toISOString(),
    note:
      'DEV deltas are not approved baseline (FR-08). Promote via DG into promotionTarget paths; no shared theme write-through from this export.',
    deltas,
    effectiveState: effective,
    effectiveCssVars: buildVclCssVars(effective),
    clamps,
  };
}

export interface VclProviderProps {
  readonly children: ReactNode;
  /**
   * Override mount gate (tests). Default: `shouldMountVcl()` =
   * `import.meta.env.DEV && (?vcl=1 || localStorage opt-in)`.
   */
  readonly forceMount?: boolean;
  /** Target for CSS vars; defaults to `document.documentElement`. */
  readonly rootElement?: HTMLElement | null;
}

/**
 * DEV-only VCL control plane. Production / non-opt-in: children only, no panel, no storage.
 */
export function VclProvider({
  children,
  forceMount,
  rootElement,
}: VclProviderProps): JSX.Element {
  const mount = forceMount === true || shouldMountVcl();
  const [state, setState] = useState<VclControlState>(() => {
    if (!mount) {
      return createBaselineState();
    }
    return readVclStateFromStorage() ?? createBaselineState();
  });

  const effectiveResult = getEffectiveState(state);

  useEffect(() => {
    if (!mount) {
      return;
    }
    const el = rootElement ?? (typeof document !== 'undefined' ? document.documentElement : null);
    if (el === null) {
      return;
    }
    applyVclCssVars(el, state, { surface: 'customer', enabled: true });
    writeVclStateToStorage(state);
    return () => {
      applyVclCssVars(el, VCL_BASELINE_STEPS, { surface: 'customer', enabled: false });
    };
  }, [mount, rootElement, state]);

  if (!mount) {
    return <>{children}</>;
  }

  const value: VclContextValue = {
    enabled: true,
    state,
    effectiveState: effectiveResult.state,
    clamps: effectiveResult.clamps,
    setControl: (id, step) => {
      setState((prev) => ({ ...prev, [id]: step }));
    },
    resetToBaseline: () => {
      setState(createBaselineState());
      clearVclStateStorage();
    },
    exportPromotionJson: () =>
      JSON.stringify(
        buildExportPayload(state, effectiveResult.state, effectiveResult.clamps),
        null,
        2,
      ),
  };

  return (
    <VclContext.Provider value={value}>
      {children}
      <VclDevPanel isBaseline={isBaselineState(effectiveResult.state)} />
    </VclContext.Provider>
  );
}